import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Button,
  useMediaQuery,
  Typography,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Menu as MenuIcon, Close as CloseIcon } from "@material-ui/icons";
import { Link, useLocation } from "react-router-dom";

const LOGO_SRC = "https://artistimages.blob.core.windows.net/montestory/logo 2.svg";

// Plain links in the middle; Login (secondary) and School Registration (primary)
// are rendered as pills on the right.
const NAV_LINKS = [
  { label: "Home", path: "/montessori/home" },
  { label: "About us", path: "/montessori/about" },
  { label: "Problems we solve", path: "/montessori/problenwesolve" },
];
const LOGIN_LINK = { label: "Log in", path: "/montessori/login" };
const REGISTER_LINK = { label: "School registration", path: "/montessori/schoolregistration" };

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: "#fff",
    color: "#1a1a1a",
    borderBottom: "1px solid #d9d9d9",
    boxShadow: "none",
    zIndex: theme.zIndex.drawer + 1,
  },
  toolbar: {
    maxWidth: 1240,
    width: "100%",
    margin: "0 auto",
    padding: theme.spacing(0, 3),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0, 2),
    },
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    textDecoration: "none",
    color: "inherit",
  },
  logoImage: {
    height: 40,
    width: "auto",
  },
  brandText: {
    fontSize: "1.35rem",
    fontWeight: 700,
    letterSpacing: "-0.02em",
    color: "#1a1a1a",
  },
  grow: { flexGrow: 1 },
  nav: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginRight: theme.spacing(2),
  },
  navLink: {
    position: "relative",
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: "0.9rem",
    fontWeight: 500,
    color: "#444",
    textDecoration: "none",
    transition: "background-color 150ms ease, color 150ms ease",
    "&:hover": { backgroundColor: "#f0f0f0", color: "#1a1a1a" },
    "&:focus-visible": { outline: "2px solid #1a1a1a", outlineOffset: 2 },
  },
  navLinkActive: {
    color: "#1a1a1a",
    fontWeight: 600,
    "&::after": {
      content: '""',
      position: "absolute",
      left: 14,
      right: 14,
      bottom: 2,
      height: 2,
      borderRadius: 2,
      backgroundColor: "#1a1a1a",
    },
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  pill: {
    textTransform: "none",
    borderRadius: 999,
    height: 40,
    padding: "0 20px",
    fontSize: "0.9rem",
    fontWeight: 500,
    boxShadow: "none",
  },
  pillPrimary: {
    backgroundColor: "#121212",
    color: "#fff",
    "&:hover": { backgroundColor: "#333", boxShadow: "none" },
  },
  pillSecondary: {
    border: "1px solid #1a1a1a",
    color: "#1a1a1a",
    backgroundColor: "#fff",
    "&:hover": { backgroundColor: "#f0f0f0" },
  },
  drawerPaper: { width: 300, backgroundColor: "#fff" },
  drawerHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2),
    borderBottom: "1px solid #d9d9d9",
  },
  drawerItem: {
    padding: theme.spacing(1.5, 3),
    "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: "#e6e6e6" },
    "&:hover": { backgroundColor: "#f0f0f0" },
  },
  drawerLabel: { fontSize: "1rem", fontWeight: 500, color: "#1a1a1a" },
  drawerActions: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 3),
    marginTop: "auto",
    borderTop: "1px solid #d9d9d9",
  },
}));

const LoginHeader = () => {
  const classes = useStyles();
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeDrawer = () => setMobileOpen(false);
  const isActive = (path) => location.pathname.startsWith(path);

  const brand = (
    <Link to="/montessori/home" className={classes.brand} aria-label="Montestory home">
      <img src={LOGO_SRC} alt="" className={classes.logoImage} />
      <Typography component="span" className={classes.brandText}>
        Montestory
      </Typography>
    </Link>
  );

  return (
    <AppBar position="fixed" className={classes.appBar}>
      <Toolbar className={classes.toolbar}>
        {brand}
        <div className={classes.grow} />

        {!isMobile ? (
          <>
            <nav className={classes.nav} aria-label="Primary">
              {NAV_LINKS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={isActive(item.path) ? "page" : undefined}
                  className={`${classes.navLink} ${isActive(item.path) ? classes.navLinkActive : ""}`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className={classes.actions}>
              <Button
                component={Link}
                to={LOGIN_LINK.path}
                variant="outlined"
                className={`${classes.pill} ${classes.pillSecondary}`}
              >
                {LOGIN_LINK.label}
              </Button>
              <Button
                component={Link}
                to={REGISTER_LINK.path}
                variant="contained"
                className={`${classes.pill} ${classes.pillPrimary}`}
              >
                {REGISTER_LINK.label}
              </Button>
            </div>
          </>
        ) : (
          <IconButton edge="end" aria-label="Open menu" onClick={() => setMobileOpen(true)}>
            <MenuIcon style={{ color: "#1a1a1a" }} />
          </IconButton>
        )}
      </Toolbar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={closeDrawer}
        classes={{ paper: classes.drawerPaper }}
      >
        <div className={classes.drawerHead}>
          {brand}
          <IconButton aria-label="Close menu" onClick={closeDrawer}>
            <CloseIcon />
          </IconButton>
        </div>
        <List>
          {NAV_LINKS.map((item) => (
            <ListItem
              button
              key={item.path}
              component={Link}
              to={item.path}
              selected={isActive(item.path)}
              onClick={closeDrawer}
              className={classes.drawerItem}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ className: classes.drawerLabel }}
              />
            </ListItem>
          ))}
        </List>
        <div className={classes.drawerActions}>
          <Button
            component={Link}
            to={LOGIN_LINK.path}
            variant="outlined"
            onClick={closeDrawer}
            className={`${classes.pill} ${classes.pillSecondary}`}
          >
            {LOGIN_LINK.label}
          </Button>
          <Button
            component={Link}
            to={REGISTER_LINK.path}
            variant="contained"
            onClick={closeDrawer}
            className={`${classes.pill} ${classes.pillPrimary}`}
          >
            {REGISTER_LINK.label}
          </Button>
        </div>
      </Drawer>
    </AppBar>
  );
};

export default LoginHeader;
